import { t } from 'i18next';

import { SOCIAL_LINKS } from '~screens/Dashboard/components/Footer/constants';

import { BASE_URL, CURRENT_COUNTRY_ID } from './environment';
import { logoImage } from './assets';
import { phones, currencies } from './contact';

export const configCountries = {
  pa: { id: 'PA', name: 'Panama', currency: 'USD' },
  gt: { id: 'GT', name: 'Guatemala', currency: 'GTQ' },
  cr: { id: 'CR', name: 'Costa Rica', currency: 'CRC' }
};

const COUNTRY_ALPHA2 = {
  pa: 'PA',
  gt: 'GT',
  cr: 'CR'
};

export function getStructuredDataOrg() {
  const logoUrl = `${BASE_URL}${logoImage}`;
  const socialLinks = SOCIAL_LINKS ? Object.values(SOCIAL_LINKS).filter(Boolean) : [];

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: 'Papa Johns',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: logoUrl,
      width: 180,
      height: 180
    },
    image: logoUrl,
    description: t('App:SEOdescription'),
    sameAs: socialLinks,
    potentialAction: {
      '@type': 'OrderAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/?order=1`
      }
    }
  };
}

export function getStructuredDataBreadcrumbs(items) {
  if (!items || items.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.href && { item: `${BASE_URL}${item.href}` })
    }))
  };
}

export function getStructuredDataRestaurant(store) {
  if (!store) {
    return null;
  }

  const logoUrl = `${BASE_URL}${logoImage}`;

  const openingHours =
    store.hours?.flatMap(h =>
      h.configHours?.map(ch => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ch.day,
        opens: ch.opening?.split('T')[1]?.substring(0, 5) || ch.opening,
        closes: ch.closing?.split('T')[1]?.substring(0, 5) || ch.closing
      }))
    ) || [];

  return {
    '@context': 'https://schema.org',
    '@type': 'FastFoodRestaurant',
    '@id': `${BASE_URL}/stores/${store.slug}/#restaurant`,
    name: `Papa Johns - ${store.name}`,
    url: `${BASE_URL}/stores/${store.slug}`,
    telephone: store.phone || phones[CURRENT_COUNTRY_ID],
    image: logoUrl,
    servesCuisine: 'Pizza',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: store.address,
      addressLocality: store.locality,
      addressRegion: store.region,
      addressCountry: COUNTRY_ALPHA2[CURRENT_COUNTRY_ID]
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: store.latitude,
      longitude: store.longitude
    },
    ...(openingHours.length > 0 && { openingHoursSpecification: openingHours }),
    parentOrganization: {
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`,
      name: 'Papa Johns'
    }
  };
}

export function getStructuredDataMenu(menuSections) {
  const currency = currencies[CURRENT_COUNTRY_ID] || 'USD';

  if (!menuSections || typeof menuSections !== 'object') {
    return null;
  }

  const sections = Object.keys(menuSections)
    .map(sectionKey => {
      const items = menuSections[sectionKey];
      if (!Array.isArray(items) || items.length === 0) {
        return null;
      }

      const menuItems = items
        .filter(item => !item?.menuable?.hidden && !item?.menuable?.productHide)
        .slice(0, 100)
        .map((item, index) => {
          const name = item.menuable?.name || item.name;
          const rawDescription = item.menuable?.description || item.description || '';
          const description = rawDescription.replace(/\s+/g, ' ').trim();
          const image = item.menuable?.photo?.url;
          const price = item.variants?.[0]?.price || item.price;

          if (!name) {
            return null;
          }

          const product = {
            '@type': 'Product',
            name: name.trim(),
            brand: {
              '@type': 'Brand',
              name: 'Papa Johns'
            },
            ...(description && { description }),
            ...(image && { image })
          };

          const numericPrice = parseFloat(String(price).replace(/[^0-9.]/g, ''));
          if (!isNaN(numericPrice)) {
            product.offers = {
              '@type': 'Offer',
              price: numericPrice,
              priceCurrency: currency,
              availability: 'https://schema.org/InStock'
            };
          }

          return {
            '@type': 'ListItem',
            position: index + 1,
            item: product
          };
        })
        .filter(Boolean);

      if (menuItems.length === 0) {
        return null;
      }

      const sectionName = t(`Menu:${sectionKey}`);

      return {
        name: sectionName,
        itemListElement: menuItems
      };
    })
    .filter(Boolean);

  const allProducts = sections.reduce((acc, section) => [...acc, ...section.itemListElement], []);

  const flatItemListElement = allProducts.map((li, index) => ({
    ...li,
    position: index + 1
  }));

  if (flatItemListElement.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Papa Johns Menu',
    numberOfItems: flatItemListElement.length,
    itemListElement: flatItemListElement
  };
}

export function getStructuredDataFAQ(questions) {
  if (!questions?.length) {
    return null;
  }

  const mainEntity = questions
    .filter(q => q.question && q.answer)
    .map(q => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer.replace(/<[^>]*>/g, '')
      }
    }));

  if (!mainEntity.length) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity
  };
}

export function getStructuredDataItemList(name, items) {
  if (!items || items.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: item.url
    }))
  };
}

export function getStructuredDataStoreList(stores) {
  if (!stores || stores.length === 0) {
    return null;
  }

  return getStructuredDataItemList(
    'Papa Johns Stores',
    stores.map(store => ({
      name: `Papa Johns - ${store.name}`,
      url: `${BASE_URL}/stores/${store.slug}`
    }))
  );
}

export function getStructuredDataProduct(product, currency, url) {
  if (!product) {
    return null;
  }

  const hasPrice = product.price !== undefined && product.price !== null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || product.name,
    brand: {
      '@type': 'Brand',
      name: 'Papa Johns'
    },
    image: product.image?.large || product.image,
    url: url || (typeof window === 'undefined' ? '' : window.location.href),
    offers: {
      '@type': 'Offer',
      ...(hasPrice && { price: product.price }),
      priceCurrency: currency,
      availability: hasPrice ? 'https://schema.org/InStock' : 'https://schema.org/OnlineOnly',
      url: url || (typeof window === 'undefined' ? '' : window.location.href)
    }
  };
}
